import * as z from "zod";
import { IS_DEV } from "@/config/env";

/**
 * =========================
 * Helpers (DO NOT change PROD behavior)
 * =========================
 */
const reqString = (msg: string) =>
  IS_DEV ? z.string().optional() : z.string().min(1, msg);

/**
 * =========================
 * File validation constants
 * =========================
 */
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
] as const;

/**
 * =========================
 * Lawyer Verification Schema
 * =========================
 */
export const lawyerVerificationSchema = z.object({
  barCouncil: reqString("Please select a Bar Council"),

  // Renamed from idNo to registrationNumber
  registrationNumber: reqString("Enrollment Number is required"),

  year: IS_DEV
    ? z.string().optional()
    : z
        .string()
        .min(1, "Year of Enrollment is required")
        .refine((val) => {
          const year = Number(val);
          return (
            !Number.isNaN(year) &&
            year >= 1950 &&
            year <= new Date().getFullYear()
          );
        }, {
          message: `Year must be between 1950 and ${new Date().getFullYear()}`,
        }),

  // New field replacing stateOfIssue
  courtPractice: IS_DEV
    ? z.array(z.string()).optional()
    : z.array(z.string()).min(1, "Please select at least one Court of Practice"),

  certificate: IS_DEV
    ? z.custom<FileList>().optional()
    : z
        .custom<FileList>()
        .refine(
          (files) => !!files && files.length > 0,
          "Certificate or ID Card is required"
        )
        .refine(
          (files) => files?.[0]?.size <= MAX_FILE_SIZE,
          "File size must be less than 5MB"
        )
        .refine(
          (files) =>
            ACCEPTED_FILE_TYPES.includes(files?.[0]?.type as any),
          "Only PDF, JPG, and PNG files are accepted"
        ),
});

/**
 * =========================
 * Form Data Type
 * =========================
 */
export type LawyerVerificationFormData = z.infer<
  typeof lawyerVerificationSchema
>;

/**
 * =========================
 * Indian States List
 * =========================
 */
export const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Delhi",
] as const;